import React, { useEffect, useRef, useState } from "react";
import { ProductDetails } from "web/react/components/product-card/react/emo";
import ProductCard from "web/react/components/product-card/react/product-card";
import { Rail } from "web/react/components/rail/rail";
import { HStack } from "web/react/emo/h-stack";
import { ProductCardPrice, ProductCardWishlist, ProductImage } from "web/react/emo/product-card";
import { VStack } from "web/react/emo/v-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import { FeedPage } from "web/react/pages/feed/utils";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { canUseMembership } from "web/script/modules/utils";
import { ProductCardSerializer } from "web/types/serializers";
import * as styles from "./more-like-this-rail.css";

interface MoreLikeThisRailProps {
    products: ProductCardSerializer[];
    feedType?: string;
}

const analyticsEventLabelType = {
    [FeedPage.WISH_LIST]: "wishlist_more_like_this",
    default: "is_pdp_similar_product",
};

export function MoreLikeThisRail({
    products,
    feedType = FeedPage.DEFAULT,
}: MoreLikeThisRailProps): React.ReactElement | null {
    const analyticsEventLabel =
        analyticsEventLabelType[feedType] || analyticsEventLabelType.default;

    const [sentAnalytics, setSentAnalytics] = useState(false);

    const moreLikeThisRef = useRef<HTMLDivElement | null>(null);

    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px",
            once: true,
        },
        moreLikeThisRef as any
    );

    const { isMobileViewport } = useDomViewport();

    useEffect(() => {
        if (isVisible && !sentAnalytics) {
            setSentAnalytics(true);
            analytics.event("more_like_this", "impression", analyticsEventLabel);
        }
    }, [isVisible, isMobileViewport, analyticsEventLabel, sentAnalytics]);

    if (products.length === 0) {
        return null;
    }

    return (
        <div ref={moreLikeThisRef}>
            <Rail
                title={gettext("product.buy_area.more_like_this.label") /* More like this */}
                analyticsLabel={analyticsEventLabel}
            >
                {products.map((product, i) => (
                    <ProductCard
                        key={`${product.uid}-${i}`}
                        className={styles.item}
                        product={product}
                        dataTestId="product-card"
                    >
                        <VStack spacing="xxs">
                            <ProductImage
                                feedType={feedType}
                                analyticsEventLabel={analyticsEventLabel}
                                shouldRenderLeadLinkInImage
                            />
                            <VStack spacing="xxxs">
                                <HStack spacing="none" justify="space-between">
                                    <ProductCardPrice size="sm" />
                                    {canUseMembership() && (
                                        <ProductCardWishlist
                                            isSavedForLater={product.is_saved_for_later}
                                        />
                                    )}
                                </HStack>
                                <ProductDetails
                                    feedType={feedType}
                                    analyticsEventLabel={analyticsEventLabel}
                                />
                            </VStack>
                        </VStack>
                    </ProductCard>
                ))}
            </Rail>
        </div>
    );
}
